import { useState, useRef, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  BookOpen,
  Upload,
  File,
  FileText,
  Image,
  Trash2,
  Plus,
  Search,
  FolderOpen,
  Clock,
  HardDrive,
  AlertCircle,
  X,
  RefreshCw,
  Loader2,
  ExternalLink,
  Sparkles,
  CheckCircle,
} from "lucide-react";
import { api } from "../../services/api";
import EmptyState from "../../components/EmptyState";
import Badge from "../../components/Badge";
import Dialog from "../../components/Dialog";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

export default function Knowledge() {
  const { id } = useParams();
  const [files, setFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [rejectedFiles, setRejectedFiles] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, fileId: null, fileName: "" });
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [isRetraining, setIsRetraining] = useState(false);
  const [retrainSuccess, setRetrainSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const fetchFiles = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.getKnowledgeFiles(id);
      setFiles(response.files || []);
    } catch (err) {
      console.error("Failed to fetch knowledge files:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const filteredFiles = files.filter((file) =>
    (file.name || file.original_name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getFileIcon = (type) => {
    if (!type) return File;
    if (type.includes("image")) return Image;
    if (type.includes("pdf") || type.includes("document")) return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      handleFiles(droppedFiles);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
    e.target.value = "";
  };

  const handleFiles = async (newFiles) => {
    const validFiles = [];
    const rejected = [];

    newFiles.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        rejected.push({
          name: file.name,
          size: file.size,
          reason: "File exceeds 10MB limit",
        });
      } else {
        validFiles.push(file);
      }
    });

    // Show rejected files
    if (rejected.length > 0) {
      setRejectedFiles(rejected);
      setTimeout(() => setRejectedFiles([]), 5000);
    }

    // Upload valid files
    if (validFiles.length > 0) {
      setIsUploading(true);
      try {
        for (const file of validFiles) {
          const response = await api.uploadKnowledgeFile(id, file);
          if (response.file) {
            setFiles(prev => [response.file, ...prev]);
          }
        }
      } catch (err) {
        console.error("Failed to upload files:", err);
        setError(err.message);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const dismissRejectedFiles = () => {
    setRejectedFiles([]);
  };

  const handleRemoveClick = (fileId, fileName) => {
    setDeleteDialog({ isOpen: true, fileId, fileName });
  };

  const handleConfirmDelete = async () => {
    if (deleteDialog.fileId) {
      try {
        setDeletingId(deleteDialog.fileId);
        await api.deleteKnowledgeFile(id, deleteDialog.fileId);
        setFiles(prev => prev.filter(f => f.id !== deleteDialog.fileId));
        // Close dialog only after successful deletion
        setDeleteDialog({ isOpen: false, fileId: null, fileName: "" });
      } catch (err) {
        console.error("Failed to delete file:", err);
        setError(err.message);
        // Close dialog on error too
        setDeleteDialog({ isOpen: false, fileId: null, fileName: "" });
      } finally {
        setDeletingId(null);
      }
    }
  };

  const totalSize = files.reduce((sum, file) => sum + (file.size || 0), 0);

  const handleRetrain = async () => {
    if (files.length === 0) return;

    setIsRetraining(true);
    setRetrainSuccess(false);
    setError(null);

    try {
      await api.retrainKnowledge(id);
      setRetrainSuccess(true);
      setTimeout(() => setRetrainSuccess(false), 5000);
    } catch (err) {
      console.error("Failed to retrain knowledge:", err);
      setError(err.message);
    } finally {
      setIsRetraining(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">
              Knowledge Base
            </h1>
            <p className="text-text-secondary">
              Train your AI with documents and files
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchFiles}
            disabled={isLoading}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 text-text-muted ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleRetrain}
            disabled={isRetraining || files.length === 0}
            className="px-4 py-2 rounded-xl border border-primary text-primary font-medium hover:bg-primary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isRetraining ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            {isRetraining ? "Retraining..." : "Retrain AI"}
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="btn-primary flex items-center gap-2"
          >
            {isUploading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            {isUploading ? "Uploading..." : "Add Files"}
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.jpg,.jpeg,.png"
        />
      </div>

      {/* Success State */}
      {retrainSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3 animate-slide-up">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <span className="text-green-700">Knowledge base retrained successfully! Your AI is now up to date.</span>
          <button
            onClick={() => setRetrainSuccess(false)}
            className="ml-auto text-sm text-green-600 hover:text-green-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-sm text-red-600 hover:text-red-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6 max-w-md">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <FolderOpen className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">
                {files.length}
              </p>
              <p className="text-sm text-text-muted">Total Files</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-accent/10">
              <HardDrive className="w-5 h-5 text-accent" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">
                {formatFileSize(totalSize)}
              </p>
              <p className="text-sm text-text-muted">Total Size</p>
            </div>
          </div>
        </div>
      </div>

      {/* Retrain Reminder Banner */}
      {files.length > 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Remember to Retrain AI</p>
            <p className="text-sm text-amber-700 mt-1">
              After adding or removing files, click the "Retrain AI" button to update your agent's knowledge. Files showing "pending" or "uploaded" status have not been processed yet.
            </p>
          </div>
        </div>
      )}

      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !isUploading && fileInputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-xl p-8 mb-6 text-center transition-all duration-200 cursor-pointer
          ${isUploading ? "opacity-50 cursor-not-allowed" : ""}
          ${
            isDragging
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50 hover:bg-background"
          }
        `}
      >
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mx-auto mb-4 pointer-events-none">
          {isUploading ? (
            <RefreshCw className="w-8 h-8 text-primary animate-spin" />
          ) : (
            <Upload
              className={`w-8 h-8 ${
                isDragging ? "text-primary" : "text-text-muted"
              }`}
            />
          )}
        </div>
        <h3 className="font-semibold text-text-primary mb-1 pointer-events-none">
          {isUploading ? "Uploading files..." : isDragging ? "Drop files here" : "Drag and drop files"}
        </h3>
        <p className="text-sm text-text-muted mb-4 pointer-events-none">
          or click here to browse
        </p>
        <p className="text-xs text-text-muted pointer-events-none">
          Supports PDF, DOC, TXT, CSV, XLSX, and images (Max 10MB each)
        </p>
      </div>

      {/* Rejected Files Warning */}
      {rejectedFiles.length > 0 && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl animate-slide-up">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">
                  {rejectedFiles.length} file{rejectedFiles.length > 1 ? "s" : ""} couldn't be uploaded
                </p>
                <p className="text-sm text-red-600 mt-1">
                  Files must be 10MB or less. Please compress or split larger files.
                </p>
                <ul className="mt-2 space-y-1">
                  {rejectedFiles.map((file, index) => (
                    <li key={index} className="text-sm text-red-700">
                      • {file.name} ({formatFileSize(file.size)})
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <button
              onClick={dismissRejectedFiles}
              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-100 rounded transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {files.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="No knowledge files yet"
          description="Upload documents to train your AI. It will use this information to give better, more accurate responses."
        />
      ) : (
        <>
          {/* Search */}
          <div className="mb-4 relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          {/* Files List */}
          <div className="space-y-2">
            {filteredFiles.map((file, index) => {
              const FileIcon = getFileIcon(file.content_type || file.type);
              const fileName = file.original_name || file.name || "Unnamed file";
              const fileType = file.content_type || file.type || "";
              const uploadedAt = file.uploaded_at || file.uploadedAt;

              return (
                <div
                  key={file.id}
                  className="card p-4 flex items-center gap-4 hover:border-primary/20 transition-all animate-slide-up group"
                  style={{ animationDelay: `${index * 30}ms` }}
                >
                  {/* Icon */}
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center flex-shrink-0">
                    <FileIcon className="w-6 h-6 text-primary" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    {file.url ? (
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-text-primary hover:text-primary truncate flex items-center gap-2 group/link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="truncate">{fileName}</span>
                        <ExternalLink className="w-4 h-4 flex-shrink-0 opacity-0 group-hover/link:opacity-100 transition-opacity" />
                      </a>
                    ) : (
                      <h3 className="font-medium text-text-primary truncate">
                        {fileName}
                      </h3>
                    )}
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-sm text-text-muted">
                        {formatFileSize(file.size)}
                      </span>
                      {uploadedAt && (
                        <span className="text-sm text-text-muted flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(uploadedAt)}
                        </span>
                      )}
                      {file.status && file.status !== "ready" && (
                        <Badge variant={file.status === "error" ? "danger" : "warning"} size="small">
                          {file.status}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Type Badge */}
                  <Badge variant="secondary" size="small">
                    {fileType.split("/")[1]?.toUpperCase() || "FILE"}
                  </Badge>

                  {/* Delete */}
                  <button
                    onClick={() => handleRemoveClick(file.id, fileName)}
                    disabled={deletingId === file.id}
                    className="p-2 text-text-muted hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100 disabled:opacity-50"
                  >
                    {deletingId === file.id ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      <Trash2 className="w-5 h-5" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {filteredFiles.length === 0 && searchQuery && (
            <div className="text-center py-12">
              <p className="text-text-muted">
                No files matching "{searchQuery}"
              </p>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        isOpen={deleteDialog.isOpen}
        onClose={() => !deletingId && setDeleteDialog({ isOpen: false, fileId: null, fileName: "" })}
        onConfirm={handleConfirmDelete}
        title="Remove File"
        message={`Are you sure you want to remove "${deleteDialog.fileName}"? This action cannot be undone.`}
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        isLoading={deletingId === deleteDialog.fileId}
      />
    </div>
  );
}